import { AnalyticsEvents, AppOpenedPlatform } from '@mondaygpt/analytics-events'
import { useEffect, useRef } from 'react'
import { startupScripts } from '../modules'
import {
  logout,
  setLoggingOut,
  silentRefreshAction,
  subscribeToAuthBroadcast,
  syncAccessToken,
} from '../modules/authentication'
import { selectIsLoggedIn } from '../modules/common'
import { useAppDispatch, useAppSelector } from '../modules/core'
import { initOnboardingState, peekPromoCode } from '../modules/onboarding'
import { PushNotificationService } from '../services'
import { analytics } from '../services/mixpanel/analytics-config'
import { useAnalytics } from './use-analytics'
import { installMediaUrlSelfHeal } from '../modules/core/services/media-url-self-heal.install'
import { useWeeklyEngagement } from './use-weekly-engagement'

export const useInit = () => {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const { track } = useAnalytics()
  const { recordOpen } = useWeeklyEngagement()
  const firstInit = useRef(true)

  // Expired-media self-heal: signed image/video URLs live 6h; a long-open tab
  // must re-sign them itself instead of showing broken tiles until a refresh.
  useEffect(() => installMediaUrlSelfHeal(), [])

  // One-shot auth bootstrap: silent refresh against the HttpOnly cookie. Runs once
  // per page load — outside the main effect so it doesn't re-run when isLoggedIn flips.
  useEffect(() => {
    dispatch(silentRefreshAction()).catch(() => {
      // No cookie / expired / revoked — user lands logged-out, login page handles it.
    })
  }, [dispatch])

  // Cross-tab auth sync via BroadcastChannel.
  useEffect(() => {
    const unsubscribe = subscribeToAuthBroadcast(message => {
      if (message.type === 'logout') {
        // Flip the guard so any 401-driven refresh in this tab aborts during the
        // originator's logout window — otherwise it could create a new server
        // session that survives the cross-tab logout. Cleared on next mount;
        // for the dev-only StrictMode double-mount this is safe (logout is
        // idempotent and the originator is the source of truth).
        setLoggingOut(true)
        dispatch(logout())
        // Conservatively release the guard after a short window — long enough
        // for the originator's network round-trips, short enough that a stale
        // guard doesn't strand a future legitimate refresh.
        setTimeout(() => setLoggingOut(false), 1000)
      } else if (message.type === 'token-refreshed') {
        dispatch(syncAccessToken(message.accessToken))
      }
    })
    return unsubscribe
  }, [dispatch])

  // Initialize app, auth, and push notifications
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Tracking lives inside the try so a throw from any analytics destination
        // (third-party SDKs are flaky by design) cannot block startupScripts.load
        // and brick the app.
        if (firstInit.current) {
          // First-touch promo attribution: register before APP_OPENED fires so the
          // code lands on the cohort-entry event, mirroring how UTMs are captured.
          // register_once keeps the FIRST code a device ever saw.
          const promoCode = peekPromoCode()
          if (promoCode) {
            analytics.registerSuperPropertiesOnce({ promo_code: promoCode })
          }
          track(AnalyticsEvents.Application.APP_OPENED, {
            platform: AppOpenedPlatform.WEB,
          })
          recordOpen()
        }

        dispatch(initOnboardingState())
        await dispatch(startupScripts.load(isLoggedIn))

        if (isLoggedIn) {
          await PushNotificationService.getInstance().initialize()
        }
      } catch (error) {
        console.error('Error initializing app', error)
      } finally {
        firstInit.current = false
      }
    }

    initializeApp()

    return () => {
      if (!isLoggedIn) {
        PushNotificationService.getInstance().unregisterToken()
      }
    }
  }, [isLoggedIn, dispatch, track, recordOpen])
}
